import type { ExcursionModel } from "./ExcursionModel";
import type {
  Excursion,
  ExcursionRequest,
  ExcursionParams,
  ExcursionParamsInput,
  ExcursionGroupParams,
  ExcursionGroupParamsInput,
  ExcursionBooking,
  ExcursionBookingFilters,
  CreateExcursionBookingRequest,
  UpdateExcursionBookingRequest,
} from "./Excursion.typed";
import type { MultiComment } from "../zekay/comments";
import type {
  ExcursionSMSMessagesResponse,
  SendExcursionSMSRequest,
  SendExcursionSMSResponse,
} from "../smsTemplates/SMSTemplate.typed";

export class ExcursionAdapter {
  constructor(private model: ExcursionModel) {}

  async listExcursions(from: string, to: string): Promise<Excursion[]> {
    return this.model.listExcursions(from, to);
  }

  async getExcursion(id: number): Promise<Excursion> {
    return this.model.getExcursion(id);
  }

  async getExcursionsBatch(ids: number[]): Promise<Excursion[]> {
    return this.model.getExcursionsBatch(ids);
  }

  async createExcursion(payload: ExcursionRequest): Promise<Excursion> {
    return this.model.createExcursion(payload);
  }

  async updateExcursion(id: number, payload: ExcursionRequest): Promise<Excursion> {
    return this.model.updateExcursion(id, payload);
  }

  async listExcursionParams(): Promise<ExcursionParams[]> {
    return this.model.listExcursionParams();
  }

  async getExcursionParams(): Promise<ExcursionParams | null> {
    return this.model.getExcursionParams();
  }

  async getExcursionParamsById(id: number): Promise<ExcursionParams | null> {
    return this.model.getExcursionParamsById(id);
  }

  async createExcursionParams(payload: ExcursionParamsInput): Promise<ExcursionParams> {
    return this.model.createExcursionParams(payload);
  }

  async updateExcursionParams(id: number, payload: ExcursionParamsInput): Promise<ExcursionParams> {
    return this.model.updateExcursionParams(id, payload);
  }

  async deleteExcursionParams(id: number): Promise<void> {
    return this.model.deleteExcursionParams(id);
  }

  async listExcursionGroupParams(): Promise<ExcursionGroupParams[]> {
    return this.model.listExcursionGroupParams();
  }

  async getExcursionGroupParams(): Promise<ExcursionGroupParams | null> {
    return this.model.getExcursionGroupParams();
  }

  async getExcursionGroupParamsById(id: number): Promise<ExcursionGroupParams | null> {
    return this.model.getExcursionGroupParamsById(id);
  }

  async createExcursionGroupParams(
    payload: ExcursionGroupParamsInput,
  ): Promise<ExcursionGroupParams> {
    return this.model.createExcursionGroupParams(payload);
  }

  async updateExcursionGroupParams(
    id: number,
    payload: ExcursionGroupParamsInput,
  ): Promise<ExcursionGroupParams> {
    return this.model.updateExcursionGroupParams(id, payload);
  }

  async deleteExcursionGroupParams(id: number): Promise<void> {
    return this.model.deleteExcursionGroupParams(id);
  }

  async deleteExcursion(id: number): Promise<void> {
    return this.model.deleteExcursion(id);
  }

  async updateExcursionGroupComments(
    excursionId: number,
    groupId: number,
    comments: MultiComment[],
  ): Promise<void> {
    return this.model.updateExcursionGroupComments(excursionId, groupId, comments);
  }

  async listExcursionBookings(filters?: ExcursionBookingFilters): Promise<ExcursionBooking[]> {
    return this.model.listExcursionBookings(filters);
  }

  async getExcursionBooking(id: number): Promise<ExcursionBooking> {
    return this.model.getExcursionBooking(id);
  }

  async createExcursionBooking(payload: CreateExcursionBookingRequest): Promise<ExcursionBooking> {
    return this.model.createExcursionBooking(payload);
  }

  async updateExcursionBooking(
    id: number,
    payload: UpdateExcursionBookingRequest,
  ): Promise<ExcursionBooking> {
    return this.model.updateExcursionBooking(id, payload);
  }

  async deleteExcursionBooking(id: number): Promise<void> {
    return this.model.deleteExcursionBooking(id);
  }

  async sendExcursionSMS(
    id: number,
    payload: SendExcursionSMSRequest,
  ): Promise<SendExcursionSMSResponse> {
    return this.model.sendExcursionSMS(id, payload);
  }

  async listExcursionSMSMessages(id: number): Promise<ExcursionSMSMessagesResponse> {
    return this.model.listExcursionSMSMessages(id);
  }
}
